'use client';

import {
  CSSProperties,
  forwardRef,
  MouseEvent,
  ReactNode,
} from 'react';

import {
  FloatingFocusManager,
  FloatingOverlay,
  useMergeRefs,
} from '@floating-ui/react';
import { IconStrokeCrossSmall } from '@mo/icons';
import { Icon, IconButton } from '@mo/ui';
import { ScopedPortal } from '@repo/edtech-shared';
import clsx from 'clsx';

import { ModalContext } from './modal-context';
import { useModal,UseModalOptions } from './useModal';

import st from './Modal.module.scss';

export const modalPaperPlacements = [
  'center',
  'top',
  'bottom',
] as const;
type ModalPaperPlacement = typeof modalPaperPlacements[number];

export interface ModalProps extends UseModalOptions {
  children: ReactNode,
  lockScroll?: boolean,
  fullscreen?: boolean,
  paperPlacement?: ModalPaperPlacement,
  showCloseButton?: boolean,
  closeIcon?: ReactNode,
  overlayStyle?: CSSProperties,
  overlayClassName?: string,
  paperClassName?: string,
  paperStyle?: CSSProperties,
  withFocusManager?: boolean,
}

export const ScopedModal = forwardRef<HTMLDivElement, ModalProps>(function Modal({
  children,
  lockScroll = true,
  fullscreen = false,
  showCloseButton = true,
  overlayStyle,
  overlayClassName,
  paperClassName,
  paperStyle,
  paperPlacement = 'center',
  closeIcon,
  withFocusManager = true,
  ...props
}, ref) {
  const modal = useModal(props);
  const { context: floatingContext, ...context } = modal;
  const paperRef = useMergeRefs([ context.refs.setFloating, ref ]);

  if (!floatingContext.open) return null;

  return (
    <ScopedPortal>
      <ModalContext.Provider value={modal}>
        <div className={st.wrapper}>
          <FloatingOverlay
            className={
              clsx(
                st.overlay,
                paperPlacement === 'top' && st.overlay_top,
                paperPlacement === 'center' && st.overlay_center,
                paperPlacement === 'bottom' && st.overlay_bottom,
                overlayClassName,
              )
            }
            lockScroll={lockScroll}
            style={overlayStyle}
          >
            <FloatingFocusManager
              context={floatingContext}
              disabled={!withFocusManager}
            >
              <div
                ref={paperRef as any}
                aria-describedby={context.descriptionId}
                aria-labelledby={context.labelId}
                className={
                  clsx(
                    st.paper,
                    fullscreen && st.paper_fullscreen,
                    paperClassName,
                  )
                }
                style={paperStyle}
                {...context.getFloatingProps()}
              >
                {
                  showCloseButton && (
                    <IconButton
                      className={st.close_icon}
                      colors="secondary"
                      onClick={
                        (e: MouseEvent) => context.onOpenChange(
                          false, e.nativeEvent, 'button-cross-press',
                        )
                      }
                    >
                      {closeIcon || <Icon source={IconStrokeCrossSmall} />}
                    </IconButton>
                  )
                }
                {children}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </div>
      </ModalContext.Provider>
    </ScopedPortal>
  );
});
